import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isCompleted, validate, Validator } from 'utils/Validation';
import { AuthDialog } from 'components/AuthDialog/AuthDialog';
import { RootState } from 'store';
import { useCleanUnfinishedUploads } from 'hooks/useCleanUnfinishedUploads';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { changePassword, clearEditProfile, getProfile, getSample, saveProfile } from 'store/slices/user';
import { Box, Button, SvgIconProps, TextField, Typography } from '@mui/material';
import { Add, Email, Facebook, GitHub, Instagram, LinkedIn, Remove, X, YouTube } from '@mui/icons-material';
import SocialNetwork from 'domain/SocialNetwork';
import { PictureResizedFile } from 'domain/Picture';
import Grid from '@mui/material/Unstable_Grid2';
import ErrorMessage from 'components/ErrorMessage';
import { useHistory } from 'react-router-dom';
import Uploader, { UPLOADER_CATEGORY, UPLOADER_TYPES } from '../Uploader/Uploader';
import { FileUpload } from 'store/slices/file';
import UserSocialNetwork from 'domain/UserSocialNetwork';
import { FileUploadStatuses } from '../Uploader/UploadFileAsLink';
import { useAppSelector } from 'hooks/useAppSelector';
import { Switch } from '@mui/material'

interface SocialMediaIconProps {
    network: string;
    iconProps?: SvgIconProps;
}

const SocialMediaIcon: React.FC<SocialMediaIconProps> = ({ network, iconProps }) => {
    switch (network.toLowerCase()) {
    case 'facebook':
        return <Facebook {...iconProps} />;
    case 'twitter':
    case 'x':
        return <X {...iconProps} />;
    case 'instagram':
        return <Instagram {...iconProps} />;
    case 'linkedin':
        return <LinkedIn {...iconProps} />;
    case 'youtube':
        return <YouTube {...iconProps} />;
    case 'email':
        return <Email {...iconProps} />;
    case 'github':
        return <GitHub {...iconProps} />;
    default:
        return null;
    }
}

interface ErrorSubfields {
    tempId: number,
    idSocialNetwork: string
}

const EditProfile = () => {
    useCleanUnfinishedUploads();
    const history = useHistory();
    const dispatch = useAppDispatch();

    const user = useSelector((state: RootState) => state.user);
    const auth = useSelector((state: RootState) => state.auth);
    const {
        gettingProfile,
        gettingProfileError,
        gettingProfileSuccess,
        gettingProfileData: profileData,
        updatingProfile,
        updatingProfileError,
        updatingProfileSuccess,
    } = user;
    const { email, idUser } = auth.tokenData?.data || {};

    const help = useAppSelector((state) => state.help);
    const { showHelp } = help;

    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [passwordFormError, setPasswordFormError] = useState('');
    const { changingPassword, changePasswordSuccess, changePasswordError, changePasswordErrorMsg } = user;
    const [customSocialNetworks, setCustomSocialNetworks] = useState<UserSocialNetwork[]>([]);
    const [availableSocialNetworks, setAvailableSocialNetworks] = useState<SocialNetwork[]>([]);
    const [filesUploaded, setFilesUploaded] = useState<FileUpload[]>([]);
    const [showEmail, setShowEmail] = useState<boolean>(false);
    const [, setProfilePicture] = useState<Partial<PictureResizedFile>>({} as PictureResizedFile);

    useEffect(() => {
        setShowEmail(!!availableSocialNetworks?.find((n) => n.name.toLowerCase() === 'email')?.handle)
    }, []);

    const [errors, setErrors] = useState({
        firstname: false,
        customSocialNetworks: false,
    });
    const [nameErrors, setNameErrors] = useState<ErrorSubfields[]>([])
    const [handleErrors, setHandleErrors] = useState<ErrorSubfields[]>([])

    const [showErrors, setShowErrors] = useState(false);

    const validators: Validator<string | UserSocialNetwork[]>[] = [
        {
            fieldName: 'firstname',
            fieldValue: firstname,
            isValid: isCompleted,
        },
        {
            fieldName: 'customSocialNetworks',
            fieldValue: customSocialNetworks,
            isValid: () => {
                setNameErrors([]);
                setHandleErrors([]);
                const errorFields = customSocialNetworks
                    .filter((csn) => !csn.deleted)
                    .map((csn) => {
                        const nameError: ErrorSubfields = (csn.handle || csn.idSocialNetwork) && !csn.name ? {
                            tempId: csn.tempId,
                            idSocialNetwork: csn.idSocialNetwork
                        } : null;
                        if (nameError)
                            setNameErrors((prev) => {
                                return [...prev, nameError]
                            })

                        const handleError: ErrorSubfields = (csn.name || csn.idSocialNetwork) && !csn.handle ? {
                            tempId: csn.tempId,
                            idSocialNetwork: csn.idSocialNetwork
                        } : null;
                        if (handleError)
                            setHandleErrors((prev) => {
                                return [...prev, handleError]
                            })

                        return (!!nameError) || (!!handleError)
                    }).filter((e) => e)
                return errorFields.length === 0;
            },
        },
    ];

    useEffect(() => {
        setFirstname(profileData?.firstname);
        setLastname(profileData?.lastname);
        if (!customSocialNetworks?.length)
            setCustomSocialNetworks([{ tempId: 1, idUser: '', idSocialNetwork: '' }])
        setAvailableSocialNetworks(profileData?.availableSocialNetworks?.map((asn) => ({
            ...asn,
            handle: profileData?.customSocialNetworks?.find((csn) => csn.idSocialNetwork === asn.id)?.handle
        })))
        setCustomSocialNetworks(profileData?.customSocialNetworks?.filter((csn) =>
            !profileData?.availableSocialNetworks?.find((asn) => asn.id === csn.idSocialNetwork))
        )
        setProfilePicture(profileData?.picture);
    }, [profileData]);

    useEffect(() => {
        if (!gettingProfile && !gettingProfileSuccess && !gettingProfileError) {
            if (!showHelp) {
                void dispatch(getProfile());
            } else {
                void dispatch(getSample());
            }
        }
        return () => {
            dispatch(clearEditProfile());
        }
    }, []);

    const handleAddNetwork = () => {
        setCustomSocialNetworks((prevNetworks) => {
            const maxTempId = prevNetworks.reduce(
                (maxId, network) =>
                    !isNaN(network.tempId) ? Math.max(maxId, network.tempId) : maxId,
                0
            );

            const newNetwork: UserSocialNetwork = {
                tempId: (maxTempId || 0) + 1,
                name: '',
                handle: '',
                idUser: '',
                idSocialNetwork: ''
            };

            return [...prevNetworks, newNetwork];
        });
    };

    const updateCustomNetworkName = (sn: UserSocialNetwork, name: string) => {
        setCustomSocialNetworks((prevNetworks) =>
            prevNetworks.map((network) =>
                (network.tempId && network.tempId === sn.tempId) ||
                (network.id && network.id === sn.id)
                    ? { ...network, name }
                    : network
            )
        );
    };

    const updateCustomNetworkHandle = (sn: UserSocialNetwork, handle: string) => {
        setCustomSocialNetworks((prevNetworks) =>
            prevNetworks.map((network) => {
                console.log('network', JSON.stringify(network))
                return (network.tempId && network.tempId === sn.tempId) ||
                    (network.id && network.id === sn.id)
                    ? { ...network, handle }
                    : network
            }
            )
        );
    };

    const updateAvailableSocialNetwork = (sn: SocialNetwork, handle?: string) => {
        setAvailableSocialNetworks((prevNetworks) =>
            prevNetworks.map((network) =>
                network.id === sn.id
                    ? { ...network, handle: handle ?? '' }
                    : network
            )
        );
    };

    const handleFirstnameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFirstname(event.target.value);
    };

    const handleLastnameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastname(event.target.value);
    };

    const handleRemoveNetwork = (userSocialNetwork: UserSocialNetwork) => {
        if (userSocialNetwork.tempId) {
            setCustomSocialNetworks((prevNetworks) =>
                prevNetworks.filter((network) => network.tempId !== userSocialNetwork.tempId)
            );
        } else if (userSocialNetwork.idSocialNetwork) {
            setCustomSocialNetworks((prevNetworks) => {
                console.log('here: ', JSON.stringify(prevNetworks))
                return prevNetworks.map((network) => ({
                    ...network,
                    ...((network.idSocialNetwork === userSocialNetwork.idSocialNetwork) ? {
                        deleted: true
                    } : {})
                }))
            }
            );
        }
    };

    const save = () => {
        void dispatch(
            saveProfile({
                firstname,
                lastname,
                filesUploaded,
                customSocialNetworks: customSocialNetworks.filter((csn) => !csn.deleted),
                availableSocialNetworks,
                networksToDelete: customSocialNetworks.filter((csn) => csn.deleted),
            })
        );
    };

    const avatarName = firstname || lastname
        ? `${firstname ?? ''} ${lastname ?? ''}`.trim()
        : email;

    return (
        <AuthDialog
            title={'Edit profile'}
            alternative={
                !updatingProfileSuccess && !updatingProfileError &&
                !gettingProfile && !gettingProfileError ?
                    'Change your personal details' :
                    null
            }
            processingCopy={
                updatingProfile ?
                    'Your profile is being updated...' :
                    (gettingProfile ?
                        'Loading your profile information...' :
                        null)
            }
            processing={updatingProfile || gettingProfile}
            success={updatingProfileSuccess}
            successContent={
                <Typography variant={'body1'} textAlign={'center'}>
                    Your Profile Just Got an Exciting Makeover! 🌟
                </Typography>
            }
            error={updatingProfileError || gettingProfileError}
            errorContent={<ErrorMessage/>}
            onClose={() => {
                void dispatch(clearEditProfile());
                history.push('/');
            }}
        >
            <Grid container>
                <Grid xs={12} sm={4} alignContent={'center'}>
                    <Box my={1} sx={{ textAlign: 'center' }}>
                        <Uploader
                            category={UPLOADER_CATEGORY.PROFILE}
                            type={UPLOADER_TYPES.LINK}
                            fileTypes={['image/png', 'image/jpeg']}
                            files={profileData?.picture ? [{
                                ...profileData?.picture,
                                url: profileData?.picture?.small?.url,
                                status: FileUploadStatuses.UPLOADED
                            }] : profileData?.pictureUrl ? ([{
                                url: profileData.pictureUrl,
                                status: FileUploadStatuses.UPLOADED
                            }] satisfies Partial<FileUpload>[]) : []}
                            label={avatarName}
                            onFileUploaded={(fu: FileUpload[]) => {
                                setFilesUploaded(fu);
                            }}
                            multiple={false}
                            idUser={idUser}
                            jwt={auth.jwt}
                        />
                    </Box>
                </Grid>
                <Grid xs={12} sm={8} alignContent={'center'}>
                    <Box sx={{
                        m: 1,
                        width: '100%'
                    }}
                    >
                        <TextField
                            fullWidth
                            size={'medium'}
                            label='First Name'
                            type='text'
                            value={firstname}
                            onChange={handleFirstnameChange}
                            placeholder='First Name'
                            error={showErrors && errors.firstname}
                        />
                        {showErrors && errors.firstname &&
                            <Box color='error.main' textAlign='center' mt={1}>
                                Please enter your first name.
                            </Box>
                        }
                    </Box>
                    <Box sx={{
                        m: 1,
                        width: '100%'
                    }}
                    >
                        <TextField
                            fullWidth
                            size={'medium'}
                            label='Last Name'
                            type='text'
                            value={lastname}
                            onChange={handleLastnameChange}
                            placeholder='Last Name'
                        />
                    </Box>
                </Grid>
                <Box
                    sx={{
                        padding: '2em .5em',
                        textAlign: 'center',
                        width: '100%'
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '1.1em',
                        }}
                        variant='body1'
                    >
                        Help others connect with you!
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '.9em',
                        }}
                        variant='body1'
                    >
                        Add popular networks below or click 'Add New Network' for others.
                    </Typography>
                </Box>
                <Grid container spacing={1}>
                    {availableSocialNetworks?.map((n) => (
                        <Grid xs={12} sm={6} key={n.id}>
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '12px',
                                    backgroundColor: '#f5f5f5',
                                    // border: '1px solid #ccc',
                                    minHeight: '70px',
                                }}
                            >
                                <Box sx={{ mr: 1.25, ml: 1.25, pt: 1.25 }}>
                                    <SocialMediaIcon network={n.name}/>
                                </Box>
                                <Box sx={{ flexGrow: 1, mr: 1.25 }}>
                                    {n.name.toLowerCase() === 'email' ? (
                                        <Box>
                                            <Box>
                                                <Typography variant="caption">Email</Typography>
                                                <Switch
                                                    checked={showEmail}
                                                    onChange={(e) => {
                                                        setShowEmail(e.target.checked);
                                                        updateAvailableSocialNetwork(n, e.target.checked ? email : '');
                                                    }}
                                                    color="primary"
                                                />
                                                <Typography variant="caption"
                                                    sx={{ fontSize: 12 }}
                                                >({showEmail ? 'visible' : 'hidden'})</Typography>
                                            </Box>
                                            <Typography variant="body1">{email}</Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={`${n.name} Handle`}
                                            value={n.handle}
                                            onChange={(event) => updateAvailableSocialNetwork(n, event.target.value)}
                                            placeholder={`Your ${n.name} username or profile URL`}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    ))}

                </Grid>
                <Box
                    sx={{
                        padding: '.25em .5em 1.5em',
                        textAlign: 'center',
                        width: '100%'
                    }}
                >
                    <Button
                        color='secondary'
                        variant='contained'
                        disabled={!!customSocialNetworks?.find((n) => !n.name && !n.handle)}
                        size='small'
                        onClick={handleAddNetwork}
                        sx={{
                            mt: '.75em',
                        }}
                    >
                        <Add sx={{ mr: 0.5 }}/>
                        Add New Network
                    </Button>
                </Box>
                <Box sx={{
                    mb: 3,
                    width: '100%'
                }}
                >
                    {customSocialNetworks?.filter((e) => !e.deleted).map((n) => (
                        <>
                            <Box
                                key={`${n.id}-${n.tempId}-${n.idSocialNetwork}-1`}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: {
                                        xs: 'column',
                                        sm: 'row'
                                    },
                                    alignItems: 'center'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: '100%'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            m: .5,
                                            ml: {
                                                xs: 0,
                                                sm: .5,
                                            },
                                            flexGrow: 1
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            size={'small'}
                                            label='Social Network'
                                            type='text'
                                            value={n.name}
                                            onChange={(event) => {
                                                updateCustomNetworkName(n, event.target.value);
                                            }}
                                            placeholder='Social Network'
                                            error={showErrors && errors.customSocialNetworks &&
                                                !!nameErrors?.find((e) => e.tempId === n.tempId && e.idSocialNetwork === n.idSocialNetwork)}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            m: .5,
                                            mr: {
                                                xs: 0,
                                                sm: .5,
                                            },
                                            flexGrow: 1
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            size={'small'}
                                            label='Handle'
                                            type='text'
                                            value={n.handle}
                                            onChange={(event) => {
                                                updateCustomNetworkHandle(n, event.target.value);
                                            }}
                                            placeholder='Your handle or ID'
                                            error={showErrors && errors.customSocialNetworks &&
                                                !!handleErrors?.find((e) => e.tempId === n.tempId && e.idSocialNetwork === n.idSocialNetwork)}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        m: .5,
                                        width: {
                                            xs: '100%',
                                            sm: 'auto'
                                        },
                                        textAlign: 'center'
                                    }}
                                >
                                    <Button
                                        fullWidth
                                        sx={{
                                            marginTop: '4px',
                                            maxWidth: '160px'
                                        }}
                                        color={'secondary'}
                                        variant='contained'
                                        size='small'
                                        onClick={() => handleRemoveNetwork(n)}
                                    >
                                        <Remove sx={{ mr: 0.25 }}/>
                                        Remove
                                    </Button>
                                </Box>
                            </Box>
                            {showErrors ?
                                <Box
                                    key={`${n.id}-${n.tempId}-${n.idSocialNetwork}-2`}
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            m: .5,
                                            flexGrow: 1
                                        }}
                                    >
                                        {errors.customSocialNetworks &&
                                            !!nameErrors?.find((e) => e.tempId === n.tempId && e.idSocialNetwork === n.idSocialNetwork) &&
                                            <Box color='error.main' textAlign='center' mt={1}>
                                                You need to enter a social network or remove the entry all together.
                                            </Box>}
                                        {errors.customSocialNetworks &&
                                            !!handleErrors?.find((e) => e.tempId === n.tempId && e.idSocialNetwork === n.idSocialNetwork) &&
                                            <Box color='error.main' textAlign='center' mt={1}>
                                                You need to enter a handle or remove the entry all together.
                                            </Box>}
                                    </Box>
                                </Box>
                                : null}
                        </>
                    ))}
                </Box>
            </Grid>
            <Box sx={{ width: '100%', mt: 3, mb: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1em', textAlign: 'center', mb: 2 }}>
                    Change password
                </Typography>
                <Grid container spacing={1}>
                    <Grid xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Current password"
                            type="password"
                            value={currentPassword}
                            onChange={(e) => { setCurrentPassword(e.target.value); setPasswordFormError(''); }}
                            placeholder="Current password"
                        />
                    </Grid>
                    <Grid xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="New password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => { setNewPassword(e.target.value); setPasswordFormError(''); }}
                            placeholder="New password (min 8 characters)"
                        />
                    </Grid>
                    <Grid xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Confirm new password"
                            type="password"
                            value={confirmNewPassword}
                            onChange={(e) => { setConfirmNewPassword(e.target.value); setPasswordFormError(''); }}
                            placeholder="Confirm new password"
                        />
                    </Grid>
                    {passwordFormError && (
                        <Grid xs={12}>
                            <Box color="error.main" textAlign="center">{passwordFormError}</Box>
                        </Grid>
                    )}
                    {changePasswordSuccess && (
                        <Grid xs={12}>
                            <Box color="success.main" textAlign="center">Password updated successfully.</Box>
                        </Grid>
                    )}
                    {changePasswordError && (
                        <Grid xs={12}>
                            <Box color="error.main" textAlign="center">{changePasswordErrorMsg || 'Failed to change password.'}</Box>
                        </Grid>
                    )}
                    <Grid xs={12}>
                        <Box sx={{ textAlign: 'center', pt: 1 }}>
                            <Button
                                variant="outlined"
                                disabled={changingPassword}
                                onClick={() => {
                                    if (!currentPassword || !newPassword || !confirmNewPassword) {
                                        setPasswordFormError('Please fill in all password fields.');
                                        return;
                                    }
                                    if (newPassword.length < 8) {
                                        setPasswordFormError('New password must be at least 8 characters.');
                                        return;
                                    }
                                    if (newPassword !== confirmNewPassword) {
                                        setPasswordFormError('New passwords do not match.');
                                        return;
                                    }
                                    void dispatch(changePassword({ currentPassword, newPassword }));
                                }}
                            >
                                Change password
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{
                width: '100%',
                textAlign: 'center',
            }}
            >
                <Button
                    size={'large'}
                    variant='contained'
                    onClick={() => {
                        validate(validators, setErrors)
                        if (validate(validators, setErrors)) {
                            save();
                        } else {
                            setShowErrors(true);
                        }
                    }}
                >
                    Save profile
                </Button>
            </Box>
        </AuthDialog>
    );
};

export default EditProfile;
